import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ApiError,
  api,
  casosPorImei,
  type CasoCreate,
  type CasoDetalle,
  type CasoDuplicado,
  type Categoria,
  type Contacto,
  type EstadoCaso,
  type TipoFalla,
  type TipoSolicitud,
  type UserMini,
} from "@/lib/api";
import AvisoVentasImeis from "@/components/AvisoVentasImeis";
import ClientePicker from "@/components/ClientePicker";
import { verificarVentas, type VerificacionVenta } from "@/lib/ventas";
import ContactoPicker, { normalizeName } from "@/components/ContactoPicker";
import MarcaModeloMultiPicker, { type ModeloItem } from "@/components/MarcaModeloMultiPicker";
import { ayudaNuevoCaso } from "@/components/ayuda/vistas/nuevoCaso";
import AyudaVista from "@/ui/AyudaVista";
import ConfirmDialog from "@/ui/ConfirmDialog";
import Modal from "@/ui/Modal";
import { useToast } from "@/ui/Toast";

/** Prefill para abrir el modal con datos pre-llenados.
 *
 * Casos comunes:
 * - "Caso relacionado": cliente + marca + modelo + IMEIs del caso padre
 *   + caso_padre_id (para enlazar el hilo en el backend).
 * - "Crear caso desde correo": campos extraidos por el parser de un form
 *   estructurado (Garantias, etc.) — todos los campos del formulario
 *   menos cliente_id (lo elige el user con el ClientePicker porque puede
 *   ser cliente nuevo).
 */
export type NuevoCasoPrefill = {
  cliente_id?: number;
  cliente_nombre?: string;
  marca?: string;
  modelo?: string;
  imeis_raw?: string;
  caso_padre_id?: number;
  caso_padre_folio?: string;
  contacto_nombre?: string;
  contacto_email?: string;
  contacto_telefono?: string;
  descripcion_inicial?: string;
  tipo_categoria?: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated?: (caso: CasoDetalle) => void;
  prefill?: NuevoCasoPrefill;
};

export default function NuevoCasoModal({ open, onClose, onCreated, prefill }: Props) {
  const toast = useToast();
  const nav = useNavigate();

  const [asignables, setAsignables] = useState<UserMini[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  const [clienteId, setClienteId] = useState<number | null>(null);
  // modelosItems: lista de marca/modelo para el caso. El primero es el
  // modelo primario (deriva marca/modelo del payload). Por compatibilidad con
  // el prefill y el resto del modal, marca/modelo se leen del primer item.
  const [modelosItems, setModelosItems] = useState<ModeloItem[]>([{ marca: "", modelo: "" }]);
  const [descripcion, setDescripcion] = useState("");
  // Estado inicial fijo "Abierto" — el modal ya no lo expone (era
  // legacy del CLI). Se manda en el payload para no romper la API.
  const estado: EstadoCaso = "Abierto";
  const [tipoCategoria, setTipoCategoria] = useState<string>("");
  const [tipoDetalle, setTipoDetalle] = useState("");
  const [tiposSolicitud, setTiposSolicitud] = useState<TipoSolicitud[]>([]);
  const [tiposFalla, setTiposFalla] = useState<TipoFalla[]>([]);
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  // "" = nada seleccionado, "__libre__" = escribir manualmente, N = id del catalogo
  const [tipoSolicitudId, setTipoSolicitudId] = useState<string>("");
  const [tipoFallaId, setTipoFallaId] = useState<string>("");
  const [imeisRaw, setImeisRaw] = useState("");
  // Duplicados por IMEI: aviso en vivo (dups) + confirmacion al crear
  // (confirmDups, solo cuando el user le da "Crear caso" con dups presentes).
  const [dups, setDups] = useState<CasoDuplicado[]>([]);
  // Aviso anti-goles: ¿estos IMEIs los vendimos? (informativo, no bloquea)
  const [ventasCheck, setVentasCheck] = useState<VerificacionVenta[]>([]);
  const [confirmDups, setConfirmDups] = useState<CasoDuplicado[] | null>(null);
  const [asignadoId, setAsignadoId] = useState<number | "">("");
  // El contacto del caso es un FK a la tabla `contactos`. Solo elegible
  // despues de haber seleccionado el cliente (los contactos viven en el
  // cliente). El nombre se mantiene como display y para alimentar el
  // prefill del picker cuando se abre el form inline "+ Nuevo".
  const [contactoId, setContactoId] = useState<number | null>(null);
  const [contactoNombre, setContactoNombre] = useState("");
  // Snapshot del email/telefono del prefill (cuando llegamos desde un correo
  // del cliente). Solo sirve para alimentar el form de "+ Nuevo" del picker —
  // no se guardan en el caso directamente.
  const [prefillEmail, setPrefillEmail] = useState("");
  const [prefillTel, setPrefillTel] = useState("");
  const [fechaReporte, setFechaReporte] = useState(today());

  useEffect(() => {
    if (!open) return;
    api.get<UserMini[]>("/api/users/asignables").then(setAsignables).catch(() => {});
    api
      .get<TipoSolicitud[]>("/api/tipos-solicitud")
      .then(setTiposSolicitud)
      .catch(() => {});
    api
      .get<TipoFalla[]>("/api/tipos-falla")
      .then(setTiposFalla)
      .catch(() => {});
    api
      .get<Categoria[]>("/api/categorias-caso")
      .then(setCategorias)
      .catch(() => {});
    // Aplica prefill al abrir (caso relacionado o crear desde correo)
    if (prefill) {
      if (prefill.cliente_id !== undefined) setClienteId(prefill.cliente_id);
      if (prefill.marca !== undefined || prefill.modelo !== undefined) {
        setModelosItems([{
          marca: prefill.marca ?? "",
          modelo: prefill.modelo ?? "",
        }]);
      }
      if (prefill.imeis_raw !== undefined) setImeisRaw(prefill.imeis_raw);
      // El contacto del prefill no se "elige" automaticamente — el user lo
      // confirma registrandolo o eligiendo uno existente desde el picker.
      // Lo guardamos para alimentar el form "+ Nuevo" del picker.
      if (prefill.contacto_nombre !== undefined) setContactoNombre(prefill.contacto_nombre);
      if (prefill.contacto_email !== undefined) setPrefillEmail(prefill.contacto_email);
      if (prefill.contacto_telefono !== undefined) setPrefillTel(prefill.contacto_telefono);
      if (prefill.descripcion_inicial !== undefined) setDescripcion(prefill.descripcion_inicial);
      if (prefill.tipo_categoria !== undefined) setTipoCategoria(prefill.tipo_categoria);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Chequeo de duplicados por IMEI con debounce (500ms) mientras el user
  // escribe. Es una alta: nunca hay caso propio que excluir.
  useEffect(() => {
    const imeis = parseImeis(imeisRaw).filter((s) => s.length >= 6);
    if (imeis.length === 0) {
      setDups([]);
      setVentasCheck([]);
      return;
    }
    const t = setTimeout(() => {
      casosPorImei(imeis).then(setDups).catch(() => setDups([]));
      verificarVentas(imeis).then(setVentasCheck).catch(() => setVentasCheck([]));
    }, 500);
    return () => clearTimeout(t);
  }, [imeisRaw]);

  function reset() {
    setClienteId(null);
    setModelosItems([{ marca: "", modelo: "" }]);
    setDescripcion("");
    // estado es constante "Abierto" — no necesita reset
    setTipoCategoria("");
    setTipoDetalle("");
    setTipoSolicitudId("");
    setTipoFallaId("");
    setImeisRaw("");
    setAsignadoId("");
    setContactoId(null);
    setContactoNombre("");
    setPrefillEmail("");
    setPrefillTel("");
    setFechaReporte(today());
  }

  // Hay datos no-default? Sirve para advertir al cerrar accidentalmente.
  function isDirty(): boolean {
    return (
      clienteId !== null ||
      modelosItems.some((it) => it.marca.trim() !== "" || it.modelo.trim() !== "") ||
      descripcion.trim() !== "" ||
      tipoCategoria !== "" ||
      tipoDetalle.trim() !== "" ||
      imeisRaw.trim() !== "" ||
      asignadoId !== "" ||
      contactoId !== null ||
      fechaReporte !== today()
    );
  }

  function handleClose() {
    if (isDirty()) {
      setConfirmCloseOpen(true);
    } else {
      onClose();
    }
  }

  function discardAndClose() {
    reset();
    setConfirmCloseOpen(false);
    onClose();
  }

  /** Resuelve el contacto a usar para el caso:
   *
   * - Si el user ya eligió uno con el picker (contactoId no es null),
   *   devuelve ese sin tocar nada.
   * - Si NO eligió pero hay un nombre de contacto en el prefill (caso
   *   creado desde correo de formulario), evitamos perder ese dato:
   *     1) Buscamos un contacto existente del cliente con nombre
   *        normalizado identico — si lo hay, lo usamos (dedup).
   *     2) Si no hay match, creamos uno nuevo con los datos del prefill.
   * - Si no hay nada, retorna null (el caso se crea sin contacto).
   *
   * Esto soluciona el caso silencioso: el user llena el modal, "+ Nuevo"
   * abre el sub-form pero olvida darle "Registrar y elegir", y termina
   * creando el caso sin contacto. Ahora el sistema lo resuelve solo.
   */
  async function resolverContactoIdParaSubmit(
    cid: number,
  ): Promise<number | null> {
    if (contactoId !== null) return contactoId;
    const nombrePre = (prefill?.contacto_nombre || contactoNombre || "").trim();
    if (!nombrePre) return null;

    // Cargar la lista actual de contactos del cliente para dedup.
    let lista: Contacto[] = [];
    try {
      lista = await api.get<Contacto[]>(`/api/clientes/${cid}/contactos`);
    } catch {
      lista = [];
    }
    const needle = normalizeName(nombrePre);
    const match = lista.find((c) => normalizeName(c.nombre) === needle);
    if (match) {
      toast.success(
        "Contacto reutilizado",
        `${match.nombre} ya estaba registrado en el cliente`,
      );
      return match.id;
    }

    // No hay match: crear nuevo automaticamente.
    try {
      const nuevo = await api.post<Contacto>(
        `/api/clientes/${cid}/contactos`,
        {
          nombre: nombrePre,
          email: prefillEmail.trim() || null,
          telefono: prefillTel.trim() || null,
        },
      );
      toast.success("Contacto registrado", nuevo.nombre);
      return nuevo.id;
    } catch (err) {
      // Si falla el registro del contacto, NO bloqueamos la creacion del
      // caso — solo avisamos. Mejor caso sin contacto que sin caso.
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("Caso sin contacto", `No pude registrar al contacto: ${msg}`);
      return null;
    }
  }

  async function submit(force = false) {
    if (!clienteId) {
      toast.error("Selecciona un cliente o crea uno nuevo");
      return;
    }

    // Una falla debe llevar su tipo específico (alimenta "fallas recurrentes
    // por modelo" en Estadísticas). El backend también lo valida.
    if (tipoCategoria === "falla" && tipoDetalle.trim() === "") {
      toast.error(
        "Falta el tipo de falla",
        "Elige el tipo de falla (o 'escribir manualmente') para clasificar el caso.",
      );
      return;
    }

    // Hay casos que ya comparten IMEI: interceptamos y pedimos confirmar
    // antes de crear (a menos que ya venga confirmado con force=true).
    if (!force && dups.length > 0) {
      setConfirmDups(dups);
      return;
    }

    const imeis = parseImeis(imeisRaw).map((imei) => ({ imei }));

    setSubmitting(true);
    try {
      const resolvedContactoId = await resolverContactoIdParaSubmit(clienteId);

      const marca = modelosItems[0]?.marca ?? "";
      const modelo = modelosItems[0]?.modelo ?? "";
      const payload: CasoCreate = {
        cliente_id: clienteId,
        // ticket_externo: el backend genera el TE automatico (TE-YYMMDD-NNNNNN)
        marca: marca.trim() || null,
        modelo: modelo.trim() || null,
        modelos: modelosItems.filter((it) => it.modelo.trim() !== ""),
        descripcion_inicial: descripcion.trim() || null,
        estado,
        contacto_id: resolvedContactoId,
        asignado_a_id: asignadoId === "" ? null : asignadoId,
        tipo_categoria: (tipoCategoria as CasoCreate["tipo_categoria"]) || null,
        tipo_detalle: tipoDetalle.trim() || null,
        fecha_reporte_cliente: fechaReporte || null,
        caso_padre_id: prefill?.caso_padre_id ?? null,
        imeis,
      };

      const caso = await api.post<CasoDetalle>("/api/casos", payload);
      toast.success("Caso creado", caso.folio);
      reset();
      onClose();
      if (onCreated) onCreated(caso);
      else nav(`/casos/${caso.folio}`);
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo crear el caso", msg);
    } finally {
      setSubmitting(false);
    }
  }

  const titulo = prefill?.caso_padre_id
    ? `Nuevo caso relacionado a ${prefill.caso_padre_folio ?? "caso"}`
    : "Nuevo caso";
  const descripcionModal = prefill?.caso_padre_id
    ? "Continuamos el hilo del caso anterior. Cliente, equipo e IMEIs ya están precargados — ajústalos si cambia algo."
    : "Crea un caso vinculado a un cliente. Si no existe, escribe su nombre y toca «+ Crear cliente nuevo».";

  return (
    <Modal
      open={open}
      onOpenChange={(v) => !v && handleClose()}
      title={titulo}
      description={descripcionModal}
      size="lg"
      widthClass="sm:w-[88vw] sm:max-w-4xl"
      ayuda={
        <AyudaVista
          titulo="Nuevo caso"
          {...ayudaNuevoCaso({ relacionado: Boolean(prefill?.caso_padre_id) })}
        />
      }
      footer={
        <>
          <button onClick={handleClose} className="btn-ghost">Cancelar</button>
          <button onClick={() => submit()} disabled={submitting} className="btn-primary">
            {submitting ? "Creando..." : "Crear caso"}
          </button>
        </>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="md:col-span-2">
          <Label>Cliente</Label>
          <div className="mt-1">
            <ClientePicker
              value={clienteId}
              nombrePrefill={prefill?.cliente_nombre}
              onChange={(c) => {
                const next = c?.id ?? null;
                // Si cambia el cliente, el contacto del cliente anterior
                // ya no aplica. Lo limpiamos para evitar inconsistencias.
                if (next !== clienteId) {
                  setContactoId(null);
                }
                setClienteId(next);
              }}
              autoFocus
            />
          </div>
        </div>

        <div>
          <Label>Categoría del caso</Label>
          <select
            className="input mt-1"
            value={tipoCategoria}
            onChange={(e) => {
              const v = e.target.value;
              setTipoCategoria(v);
              // Al cambiar de categoría, el detalle anterior ya no aplica:
              // limpiar ambos selectores (solicitud y falla) y el texto.
              setTipoSolicitudId("");
              setTipoFallaId("");
              setTipoDetalle("");
            }}
          >
            <option value="">— sin clasificar —</option>
            {categorias.map((c) => (
              <option key={c.id} value={c.slug}>
                {c.nombre}
              </option>
            ))}
          </select>
          <p className="text-xs text-zinc-500 mt-1">
            Afecta el asunto y la frase de apertura del correo.
          </p>
        </div>

        {tipoCategoria === "falla" && (
          <div className="md:col-span-2">
            <Label>
              Tipo de falla <span className="text-rose-400">*</span>
            </Label>
            <select
              className="input mt-1"
              value={tipoFallaId}
              onChange={(e) => {
                const v = e.target.value;
                setTipoFallaId(v);
                if (v === "" || v === "__libre__") {
                  if (v === "") setTipoDetalle("");
                  // __libre__: el user escribe en el input de abajo
                } else {
                  const id = parseInt(v);
                  const t = tiposFalla.find((x) => x.id === id);
                  if (t) setTipoDetalle(t.nombre);
                }
              }}
            >
              <option value="">— elige el tipo de falla —</option>
              {tiposFalla
                .filter((t) => t.activo)
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.nombre}
                  </option>
                ))}
              <option value="__libre__">Escribir manualmente...</option>
            </select>
            {tipoFallaId === "__libre__" && (
              <input
                className="input mt-2"
                value={tipoDetalle}
                onChange={(e) => setTipoDetalle(e.target.value)}
                placeholder="Describe el tipo de falla"
                autoFocus
              />
            )}
            {tipoDetalle.trim() === "" && (
              <p className="text-xs text-rose-400 mt-1">
                Elige el tipo de falla para poder guardar el caso.
              </p>
            )}
            <p className="text-xs text-zinc-500 mt-1">
              Alimenta el reporte de fallas recurrentes por modelo del Panel.
              El catálogo se administra en Configuración → Tipos de falla.
            </p>
          </div>
        )}

        {tipoCategoria === "solicitud" && (
          <div className="md:col-span-2">
            <Label>Tipo de solicitud</Label>
            <select
              className="input mt-1"
              value={tipoSolicitudId}
              onChange={(e) => {
                const v = e.target.value;
                setTipoSolicitudId(v);
                if (v === "" || v === "__libre__") {
                  if (v === "") setTipoDetalle("");
                  // si elige __libre__, dejamos el tipoDetalle como esta
                  // para que el user escriba en el input que aparece debajo
                } else {
                  // ID numerico → llenar tipoDetalle con el nombre del catalogo
                  const id = parseInt(v);
                  const t = tiposSolicitud.find((x) => x.id === id);
                  if (t) setTipoDetalle(t.nombre);
                }
              }}
            >
              <option value="">— elige un tipo —</option>
              {tiposSolicitud
                .filter((t) => t.activo)
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.nombre}
                  </option>
                ))}
              <option value="__libre__">Escribir manualmente...</option>
            </select>
            {tipoSolicitudId === "__libre__" && (
              <input
                className="input mt-2"
                value={tipoDetalle}
                onChange={(e) => setTipoDetalle(e.target.value)}
                placeholder="Detalle del tipo de solicitud"
                autoFocus
              />
            )}
            {tiposSolicitud.length === 0 && (
              <p className="text-xs text-zinc-500 mt-1">
                Aún no hay tipos creados. Da de alta los comunes en{" "}
                <a
                  href="/configuracion?seccion=tipos-solicitud"
                  className="text-accent-400 underline"
                >
                  Configuración → Tipos de solicitud
                </a>
                .
              </p>
            )}
          </div>
        )}

        <div className="md:col-span-2">
          <Label>Reporte del cliente</Label>
          <input
            type="date"
            className="input mt-1"
            value={fechaReporte}
            onChange={(e) => setFechaReporte(e.target.value)}
          />
          <p className="text-xs text-zinc-500 mt-1">
            Fecha en que el cliente reportó la falla. Default hoy. Sirve como ancla del SLA.
          </p>
        </div>

        <div className="md:col-span-2">
          <p className="text-xs text-zinc-500">
            El Ticket Emprenet (TE-YYMMDD-NNNNNN) se genera automático al guardar.
          </p>
        </div>

        <div className="md:col-span-2">
          <MarcaModeloMultiPicker
            value={modelosItems}
            onChange={setModelosItems}
          />
        </div>

        <div className="md:col-span-2">
          <Label>IMEIs (separados por coma)</Label>
          <input
            className="input mt-1 font-mono"
            value={imeisRaw}
            onChange={(e) => setImeisRaw(e.target.value)}
            placeholder="860369053332415, 860369053332416"
          />
          <ImeisHint raw={imeisRaw} />
          {dups.length > 0 && (
            <div className="mt-2 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-200 space-y-1">
              <p className="font-medium">
                ⚠️ {dups.length === 1 ? "Ya existe un caso" : "Ya existen casos"} con {dups.length === 1 ? "este IMEI" : "estos IMEIs"}:
              </p>
              {dups.map((d) => (
                <a
                  key={d.id}
                  href={`/casos/${d.folio}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block underline decoration-dotted hover:text-amber-100"
                >
                  {d.folio} · {d.cerrado ? `cerrado hace ${d.dias_desde_cierre} d` : d.estado}
                </a>
              ))}
              <p className="text-amber-300/80">
                Si es el mismo problema, revísalo; si es otro, continúa.
              </p>
            </div>
          )}
          <AvisoVentasImeis verificaciones={ventasCheck} />
        </div>

        <div className="md:col-span-2">
          <Label>Descripción del problema</Label>
          <textarea
            className="input mt-1 min-h-[80px]"
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            placeholder="Detalle de la falla, contexto, comentarios del cliente..."
          />
        </div>

        <div>
          <Label>Asignar a</Label>
          <select
            className="input mt-1"
            value={asignadoId}
            onChange={(e) => setAsignadoId(e.target.value ? parseInt(e.target.value) : "")}
          >
            <option value="">— sin asignar —</option>
            {asignables.map((u) => (
              <option key={u.id} value={u.id}>
                {u.nombre} ({u.rol})
              </option>
            ))}
          </select>
        </div>

        <div className="md:col-span-2 mt-2">
          <Label>Contacto del cliente (opcional)</Label>
          {clienteId === null ? (
            <p className="text-xs text-zinc-500 mt-1">
              Elige primero un cliente para asignar uno de sus contactos
              registrados (o registrar uno nuevo en su ficha).
            </p>
          ) : (
            <div className="mt-1">
              <ContactoPicker
                clienteId={clienteId}
                contactoId={contactoId}
                conQuien={contactoNombre}
                onChange={({ contactoId: cid, conQuien }) => {
                  setContactoId(cid);
                  setContactoNombre(conQuien);
                }}
                prefillFromCaso={
                  contactoNombre || prefillEmail || prefillTel
                    ? {
                        nombre: contactoNombre || null,
                        email: prefillEmail || null,
                        telefono: prefillTel || null,
                      }
                    : undefined
                }
              />
              <p className="text-xs text-zinc-500 mt-1">
                Los datos del contacto (nombre, email, teléfono) se editan
                en la ficha del cliente, no aquí.
              </p>
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirmCloseOpen}
        onOpenChange={setConfirmCloseOpen}
        title="¿Descartar caso?"
        description="Tienes datos sin guardar. Si cierras se perderán."
        confirmLabel="Descartar"
        cancelLabel="Seguir editando"
        variant="danger"
        onConfirm={discardAndClose}
      />

      {confirmDups && (
        <Modal
          open
          onOpenChange={(v) => !v && setConfirmDups(null)}
          title="Posible caso duplicado"
          size="md"
          footer={
            <>
              <button className="btn-ghost" onClick={() => setConfirmDups(null)}>
                Revisar antes
              </button>
              <button
                className="btn-primary"
                onClick={() => {
                  setConfirmDups(null);
                  void submit(true);
                }}
              >
                Crear de todos modos
              </button>
            </>
          }
        >
          <p className="text-sm text-zinc-300">
            {confirmDups.length === 1 ? "Ya existe un caso" : "Ya existen casos"} con{" "}
            {confirmDups.length === 1 ? "este IMEI" : "estos IMEIs"}:
          </p>
          <ul className="mt-2 space-y-1 text-sm">
            {confirmDups.map((d) => (
              <li key={d.id}>
                <a
                  href={`/casos/${d.folio}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent-400 hover:text-accent-300 underline"
                >
                  {d.folio}
                </a>{" "}
                · {d.cerrado ? `cerrado hace ${d.dias_desde_cierre} d` : d.estado}
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-zinc-500">
            Si es el mismo problema, abre ese caso en vez de crear uno nuevo.
          </p>
        </Modal>
      )}
    </Modal>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return <span className="text-xs uppercase tracking-wide text-zinc-400">{children}</span>;
}

// Acepta coma, espacios, tabs y saltos de linea como separador
// (paste-friendly desde Excel, Sheets, listas variadas). Dedup automatico.
function parseImeis(raw: string): string[] {
  const list = raw
    .split(/[,\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  return Array.from(new Set(list));
}

function ImeisHint({ raw }: { raw: string }) {
  const list = parseImeis(raw);
  if (list.length === 0) {
    return (
      <p className="text-xs text-zinc-500 mt-1">
        Acepta coma, espacio o salto de línea (puedes pegar desde Excel/Sheets).
      </p>
    );
  }
  return (
    <p className="text-xs text-accent-400 mt-1">
      {list.length} IMEI{list.length === 1 ? "" : "s"} detectado{list.length === 1 ? "" : "s"}
    </p>
  );
}

function today(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}
